import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import useAuth from "../../ContextProvider/AuthProvider";
import {
  Column,
  Body,
  FormHeaderBox,
  FormHeaderTitle,
  CancelButton,
  ButtonBoxStyle,
} from "../../Css";
import CustomButton from "../Common/Button";
import FormGroup from "../Common/FormGroup";
import InputField from "../Common/InputField";
import { showSnackbar } from "../Common/Snackbar";
import ApiService from "../../Api/ApiServices";
import { AddMemberFormValidation } from "./Validation";
import AutoComplete from "../Common/AutoComplete";
import { useTranslation } from "react-i18next";

const defaultValue = {
  membership: {
    id: "",
    account_id: "",
    user_id: "",
  },
  membership_num: "",
  relation_id: "",
  user_fname: "",
  user_lname: "",
  role_id: "",
  user_email: "",
  relation_name: "",
};

export default function AssignMembershipForm({
  setAddInfo,
  data,
  fetchAPIData,
  fetchAccountInfo,
}) {
  const [initialValues, setInitialValues] = useState(defaultValue);
  const [relationOptions, setRelationOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const formikRef = useRef();
  const { t } = useTranslation();
  
  // Track if component is mounted to prevent memory leaks
  const isMountedRef = useRef(true);

  // Memoize the data mapping functions
  const mapRelationships = useCallback((data) => {
    return data?.map((item) => ({
      label: item.mem_rel_type,
      value: item?.id,
    })) || [];
  }, []);

  const mapRoles = useCallback((data) => {
    return data?.map((item) => ({
      label: item.name,
      value: item?.id,
    })) || [];
  }, []);

  const getRelationships = useCallback(async () => {
    try {
      const { data } = await ApiService.relationsEmergency();
      
      if (isMountedRef.current && data?.length) {
        const mappedData = mapRelationships(data);
        setRelationOptions(mappedData);
      }
    } catch (err) {
      console.error("Error fetching relationships:", err);
      if (isMountedRef.current) {
        setErrorMessage("Failed to load relationships");
      }
    }
  }, [mapRelationships]);

  const getRoles = useCallback(async () => {
    try {
      const { data } = await ApiService.getRoles();
      
      if (isMountedRef.current && data?.length) {
        const mappedData = mapRoles(data);
        setRoleOptions(mappedData);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
      if (isMountedRef.current) {
        setErrorMessage("Failed to load roles");
      }
    }
  }, [mapRoles]);

  // Memoize initial values calculation
  const calculatedInitialValues = useMemo(() => {
    if (!data) return defaultValue;
    
    return {
      ...defaultValue,
      membership: {
        id: data?.id || "",
        account_id: data?.account_id || "",
        membership_num: data?.membership_number || "",
        user_id: data?.user_id || "",
        access_code: data?.access_code || "",
      },
      membership_num: data?.membership_number || "",
      role_id: 2,
    };
  }, [data]);

  // Update initial values when data changes
  useEffect(() => {
    if (data) {
      setInitialValues(calculatedInitialValues);
    }
  }, [calculatedInitialValues]);

  // Load data on mount
  useEffect(() => {
    getRelationships();
    getRoles();
    
    // Cleanup function to prevent memory leaks
    return () => {
      isMountedRef.current = false;
    };
  }, [getRelationships, getRoles]);

  const handleEmailBlur = useCallback(async () => {
    try {
      const formik = formikRef.current;
      if (!formik) return true;
      
      formik.setFieldTouched("user_email", true);
      
      if (formik.errors?.user_email) {
        return false;
      }
      
      const email = formik.values?.user_email;
      if (!email) return true;
      
      const payload = { email };
      const { data } = await ApiService.checkAvailabilityEmail(payload);
      
      if (data?.err) {
        formik.setFieldError("user_email", data.err);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error checking email:", error);
      const formik = formikRef.current;
      if (formik) {
        formik.setFieldError("user_email", error.response?.data?.err || error.message);
      }
      return false;
    }
  }, []);

  const onSubmit = useCallback(async (values) => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      
      const isEmailValid = await handleEmailBlur();
      if (!isEmailValid) {
        return;
      }
      
      // Remove the early return that was preventing submission
      const { data } = await ApiService.addMember(values);

      if (data?.err === "Self relation already has an account") {
        formikRef?.current?.setFieldError("relation_id", data?.err);
        return;
      }
      
      if (data?.err) {
        setErrorMessage(data.err);
        return;
      }
      
      showSnackbar({ type: 'success', message: 'Membership assigned successfully.' });
      fetchAPIData();
      fetchAccountInfo();
      setAddInfo({}); // Close the form after successful submission
      
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMessage(error.response?.data?.err || error.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [handleEmailBlur, fetchAPIData, fetchAccountInfo, setAddInfo]);

  const handleCancel = useCallback(() => {
    setAddInfo({});
  }, [setAddInfo]);

  // Memoize the form render function to prevent unnecessary re-renders
  const renderForm = useCallback(({ getFieldProps, values, isSubmitting }) => (
    <Form>
      <Box sx={FormHeaderBox}>
        <Box sx={{ borderBottom: "1px solid #FF8533", width: "100%" }}>
          <Typography variant="h6" sx={FormHeaderTitle}>
            {t("screens.accountInformations.assignMembership")}
          </Typography>
        </Box>
        {errorMessage && (
          <Box sx={{ color: "error.main" }}>
            <Typography variant="body2">{errorMessage}</Typography>
          </Box>
        )}
        <div style={{ width: "100%" }}>
          <Body style={{ gap: "10px 20px" }}>
            <Column>
              <FormGroup
                name="membership_num"
                label={`${t("screens.accountInformations.cardNumber")}`}
                requiredField
              >
                <InputField
                  inputProps={{
                    readOnly: true,
                  }}
                  {...getFieldProps("membership_num")}
                />
              </FormGroup>
            </Column>
            <Column>
              <FormGroup
                name="relation_id"
                label={`${t("screens.accountInformations.relation")}`}
                requiredField
              >
                <AutoComplete
                  {...getFieldProps("relation_id")}
                  name="relation_id"
                  options={relationOptions}
                  placeholder=""
                />
              </FormGroup>
            </Column>
            {(values?.relation_id === 9 || values?.relation_id === 7) && (
              <Column>
                <FormGroup
                  name="relation_name"
                  label={`${t("screens.accountInformations.relationName")}`}
                  requiredField
                >
                  <InputField {...getFieldProps("relation_name")} />
                </FormGroup>
              </Column>
            )}
            {values?.relation_id !== 1 && (
              <>
                <Column>
                  <FormGroup
                    name="role_id"
                    label={`${t("screens.accountInformations.permission")}`}
                    requiredField
                  >
                    <AutoComplete
                      {...getFieldProps("role_id")}
                      name="role_id"
                      options={roleOptions}
                      placeholder="Select a Permission"
                    />
                  </FormGroup>
                </Column>
                <Column>
                  <FormGroup
                    name="user_email"
                    label={`${t("screens.formData.email")}`}
                    requiredField
                  >
                    <InputField
                      {...getFieldProps("user_email")}
                      onBlur={handleEmailBlur}
                    />
                  </FormGroup>
                </Column>
                <Column>
                  <FormGroup
                    name="user_fname"
                    label={`${t("screens.formData.firstName")}`}
                    requiredField
                  >
                    <InputField {...getFieldProps("user_fname")} />
                  </FormGroup>
                </Column>
                <Column>
                  <FormGroup
                    name="user_lname"
                    label={`${t("screens.formData.lastName")}`}
                    requiredField
                  >
                    <InputField {...getFieldProps("user_lname")} />
                  </FormGroup>
                </Column>
              </>
            )}
          </Body>
        </div>
        <Box sx={ButtonBoxStyle}>
          <Box display="flex">
            <CustomButton
              label={`${t("screens.formData.cancel")}`}
              type="button"
              style={CancelButton}
              onClick={handleCancel}
              disabled={isLoading}
            />
            <CustomButton
              label={
                isSubmitting || isLoading
                  ? `${t("screens.formData.submitting")}`
                  : `${t("screens.formData.submit")}`
              }
              type="submit"
              disabled={isLoading}
            />
          </Box>
        </Box>
      </Box>
    </Form>
  ), [errorMessage, t, relationOptions, roleOptions, handleEmailBlur, handleCancel, isLoading]);

  return (
    <Box sx={{ padding: "0 25px" }}>
      <Formik
        initialValues={initialValues}
        innerRef={formikRef}
        onSubmit={onSubmit}
        validationSchema={AddMemberFormValidation}
        validateOnMount={false}
        enableReinitialize
      >
        {renderForm}
      </Formik>
    </Box>
  );
}
